import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";

function renderMessage(text: string) {
  const parts = text.split(/(\[.*?\]\(.*?\))/g);
  return parts.map((part, i) => {
    const match = part.match(/\[(.*?)\]\((.*?)\)/);
    if (match) {
      const [, linkText, url] = match;
      const isWhatsApp = url.includes('wa.me') || linkText.toLowerCase().includes('whatsapp');
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={
            isWhatsApp
              ? "inline-block bg-green-600 text-white px-2 py-0.5 rounded text-xs font-semibold hover:bg-green-700 transition-colors"
              : "text-red-400 underline hover:text-red-300"
          }
        >
          {linkText}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<{ from: "user" | "bot"; text: string }[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  async function handleOpen() {
    if (!open) {
      setOpen(true);
      if (!sessionStarted) {
        try {
          const data = await api.post<{ reply: string; sessionId: string }>("/chat/start", {});
          setTyping(true);
          setTimeout(() => {
            setMessages([{ from: "bot", text: data.reply }]);
            setTyping(false);
            setSessionStarted(true);
          }, 800);
        } catch {
          setMessages([
            {
              from: "bot",
              text: "👋 Hello! I'm your **August Auto Agencies** assistant.\n\nType **help** for options or ask me anything!",
            },
          ]);
          setSessionStarted(true);
        }
      }
    } else {
      handleClose();
    }
  }

  async function handleClose() {
    if (sessionStarted) {
      try {
        const data = await api.post<{ reply: string; sessionEnded: boolean }>("/chat/end", {});
        setMessages((prev) => [...prev, { from: "bot", text: data.reply }]);
        setSessionStarted(false);
      } catch {
        setSessionStarted(false);
      }
    }
    setOpen(false);
  }

  async function sendMessage() {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((prev) => [...prev, { from: "user", text: msg }]);
    setInput("");
    setSending(true);
    setTyping(true);

    const typingDelay = Math.min(2000, 600 + msg.length * 30);
    await new Promise((r) => setTimeout(r, typingDelay));

    try {
      const data = await api.post<{ reply: string }>("/chat", { message: msg });
      setMessages((prev) => [...prev, { from: "bot", text: data.reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "Sorry, something went wrong. Please try again." },
      ]);
    } finally {
      setTyping(false);
      setSending(false);
    }
  }

  return (
    <>
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-red-700/90 text-white shadow-lg shadow-red-900/30 backdrop-blur-sm hover:bg-red-500 hover:shadow-red-500/40 transition-all duration-300"
        aria-label="Chat with us"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 h-96 border border-white/20 rounded-xl shadow-2xl flex flex-col overflow-hidden backdrop-blur-xl bg-white/70">
          <div className="bg-red-700/80 text-white px-4 py-3 font-display font-semibold text-sm flex items-center justify-between backdrop-blur-md">
            <span>✨ August Auto Assistant</span>
            <button onClick={handleClose} className="text-white/70 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {messages.length === 0 && (
              <div className="text-steel-600 text-center mt-8">
                {user
                  ? "Ask me about products, prices, location or WhatsApp!"
                  : "Ask me about location, contact or WhatsApp. Prices are for logged‑in members."}
              </div>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`max-w-[80%] px-3 py-2 rounded-lg whitespace-pre-line backdrop-blur-sm ${
                  msg.from === "user"
                    ? "bg-red-100/70 text-steel ml-auto border border-red-200/50"
                    : "bg-white/60 text-steel mr-auto border border-white/40"
                }`}
              >
                {renderMessage(msg.text)}
              </div>
            ))}

            {typing && (
              <div className="flex items-center gap-1 bg-white/60 backdrop-blur-sm max-w-fit px-3 py-2 rounded-lg mr-auto">
                <span className="h-1.5 w-1.5 rounded-full bg-steel/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-steel/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-steel/40 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            )}

            <div ref={chatEndRef} />
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="flex items-center gap-2 p-3 border-t border-white/20 bg-white/40 backdrop-blur-md"
          >
            <input
              type="text"
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="flex-1 h-9 rounded-full border border-white/30 bg-white/50 px-3 text-sm text-steel placeholder:text-steel-600/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 backdrop-blur-sm"
            />
            <button
              type="submit"
              disabled={sending || typing}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-red-700/90 text-white hover:bg-red-500 hover:shadow-md hover:shadow-red-500/30 transition-all duration-300 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}